import { z } from "zod";
import type { AcCardStatus } from "./accreditation";
import type { CardState } from "./e-accreditation";

/**
 * Sector 4 · Contingent Dashboard (user, 2026-10-22; after SUKMA's Contingent
 * Management Module). One delegation (games × organisation): a NOC its own,
 * ADMIN / SUPER_ADMIN any (the picker). Every number is a count of PEOPLE
 * (the organisation's cards) on ONE per-card readiness builder, and every
 * tile filters the readiness table to exactly those people — a count and its
 * list agree (`contingent.queries.ts`).
 *
 *   GET /contingent/summary?games_id&responsible_organization_id   the tiles
 *   GET /contingent/people?…&filter&q&cursor&limit                  the table (keyset)
 *
 * Staged as the `contingent` NOC module (Sector 4).
 */

/**
 * The tiles = the table's filters. Each is a predicate over one card:
 *   all              every card of the delegation
 *   approved         status APPROVED
 *   review           SUBMITTED or PENDING
 *   returned         RETURN
 *   no_photo         no accreditation photo
 *   to_print         card NOT_PRINTED (approved, waiting to print)
 *   card_active      card ACTIVE
 *   arriving_today / arriving_tomorrow / departing_today
 *                    the leg's expected time (else its travel date) on that local day of the games
 *   delayed          an arrival or departure still to come whose flight is DELAYED or CANCELLED
 *   no_arrival / no_departure   APPROVED, no leg of that direction
 *   needs_transfer   APPROVED, arrival still to come, needs transport, no live transfer with a driver (travel `transfer` atom)
 *   transfer_booked  arrival needs transport and its transfer has a driver
 *   in_house         checked in to a bed, not checked out
 *   no_bed           APPROVED, no open bed
 *   no_hall          has an open bed whose building no dining hall of the games serves
 *   not_ready        travel's NOT READY (`notReadySql`): an arrival still to come with a card / transfer / bed problem
 */
export const CONTINGENT_FILTERS = [
	"all",
	"approved",
	"review",
	"returned",
	"no_photo",
	"to_print",
	"card_active",
	"arriving_today",
	"arriving_tomorrow",
	"departing_today",
	"delayed",
	"no_arrival",
	"no_departure",
	"needs_transfer",
	"transfer_booked",
	"in_house",
	"no_bed",
	"no_hall",
	"not_ready",
] as const;
export type ContingentFilter = (typeof CONTINGENT_FILTERS)[number];

export const CONTINGENT_PAGE_SIZE_DEFAULT = 25;
export const CONTINGENT_PAGE_SIZE_MAX = 100;
export const CONTINGENT_SEARCH_MAX = 100;

const uuid = z.string().uuid();

/** `GET /contingent/summary`. Admins must name the organisation (422); a NOC may omit it when its scope names exactly one. */
export const contingentQuery = z.object({
	games_id: uuid,
	responsible_organization_id: uuid.optional(),
});
export type ContingentQuery = z.infer<typeof contingentQuery>;

/** `GET /contingent/people` — keyset by (name, card id); the first page carries `total`. */
export const contingentPeopleQuery = contingentQuery.extend({
	filter: z.enum(CONTINGENT_FILTERS).default("all"),
	q: z.string().trim().min(1).max(CONTINGENT_SEARCH_MAX).optional(),
	cursor: z.string().trim().min(1).max(500).optional(),
	limit: z.coerce.number().int().min(1).max(CONTINGENT_PAGE_SIZE_MAX).default(CONTINGENT_PAGE_SIZE_DEFAULT),
});
export type ContingentPeopleQuery = z.infer<typeof contingentPeopleQuery>;
export type ContingentPeopleQueryInput = z.input<typeof contingentPeopleQuery>;

/** `GET /contingent/summary` — one count per filter, plus the delegation's open room tickets. */
export interface ContingentSummary {
	games_id: string;
	organization: { id: string; name: string; code: string | null };
	/** The games' local day the "today" tiles are about (YYYY-MM-DD) and its zone. */
	date: string;
	timezone: string;
	counts: Array<{ filter: ContingentFilter; count: number }>;
	open_tickets: number;
}

/** Whether an arrival that needs transport has a transfer: none, booked without a driver yet, ready (driver), or a driver to re-check. NULL = needs none. */
export const CONTINGENT_TRANSFER_STATES = ["none", "booked", "ready", "recheck"] as const;
export type ContingentTransferState = (typeof CONTINGENT_TRANSFER_STATES)[number];

export interface ContingentLeg {
	expected_at: string | null;
	travel_date: string | null;
	mode: string;
	service_no: string | null;
	flight_status: string | null;
}

/** One person of the readiness table. */
export interface ContingentPerson {
	ac_card_id: string;
	name: string;
	category: { code: string; name: string };
	/** The card's function (sub function), when set. */
	sub_function: { name: string } | null;
	status: AcCardStatus;
	card_state: CardState | null;
	has_photo: boolean;
	arrival: ContingentLeg | null;
	departure: ContingentLeg | null;
	/** NULL when the arrival needs no transport (or there is no arrival). */
	transfer: ContingentTransferState | null;
	bed: { building: string; checked_in: boolean } | null;
	hall: string | null;
	/** travel's NOT READY for the arrival (`not_ready` filter). */
	not_ready: boolean;
}

export interface ContingentPeoplePage {
	items: ContingentPerson[];
	next_cursor: string | null;
	/** First page only (null on later pages). */
	total: number | null;
}
